import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { DialogSimpleComponentLayout } from '../../../../core/layout/dialog-simple/dialog-simple.component';
import {
  CurrenciesService,
  DivisionDTO,
  DivisionsService,
  EntityLookup,
  EUTypesService,
  LegalEntitiesService,
  LegalEntityDTO,
  LookupDTO,
  ProductGroupDTO,
  ProductGroupsService,
  ProjectDTO,
  ProjectsService,
  ProjectTypesService,
} from '../../../../openapi';

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

export interface CreateCalculationDialogResult {
  projectId: number | null;
  name: string | null;
  variant: string | null;
  defaultCurrencyId: number | null;
  legalEntityId: number | null;
  divisionId: number | null;
  productGroupId: number | null;
  projectTypeId: number | null;
  euTypeId: number | null;
  importFile: File | null;
}

@Component({
  selector: 'adf-create-calculation-dialog',
  imports: [DialogSimpleComponentLayout, ReactiveFormsModule, TranslateModule],
  templateUrl: './create-calculation-dialog.component.html',
  styleUrl: './create-calculation-dialog.component.scss',
})
export class CreateCalculationDialogComponent implements OnInit {
  private readonly _dialogRef = inject(DialogRef<CreateCalculationDialogResult | null>);
  private readonly _fb = inject(FormBuilder);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _projectsService = inject(ProjectsService);
  private readonly _projectTypesService = inject(ProjectTypesService);
  private readonly _euTypesService = inject(EUTypesService);
  private readonly _currenciesService = inject(CurrenciesService);
  private readonly _divisionsService = inject(DivisionsService);
  private readonly _legalEntitiesService = inject(LegalEntitiesService);
  private readonly _productGroupsService = inject(ProductGroupsService);

  dialogData = inject(DIALOG_DATA);

  readonly maxFileSizeBytes = MAX_FILE_SIZE_BYTES;

  projects: ProjectDTO[] = [];
  currencies: EntityLookup[] = [];
  projectTypes: LookupDTO[] = [];
  euTypes: LookupDTO[] = [];

  divisions: DivisionDTO[] = [];
  legalEntities: LegalEntityDTO[] = [];
  productGroups: ProductGroupDTO[] = [];
  filteredDivisions: DivisionDTO[] = [];
  filteredProductGroups: ProductGroupDTO[] = [];

  selectedFile: File | null = null;
  fileError: string | null = null;
  isDragOver = false;

  form = this._fb.group({
    projectId: this._fb.control<number | null>(null, Validators.required),
    name: this._fb.control<string | null>('', Validators.required),
    variant: this._fb.control<string | null>('', Validators.required),
    defaultCurrencyId: this._fb.control<number | null>(null, Validators.required),
    legalEntityId: this._fb.control<number | null>(null, Validators.required),
    divisionId: this._fb.control<number | null>({ value: null, disabled: true }, Validators.required),
    productGroupId: this._fb.control<number | null>({ value: null, disabled: true }, Validators.required),
    projectTypeId: this._fb.control<number | null>(null, Validators.required),
    euTypeId: this._fb.control<number | null>(null, Validators.required),
  });

  get f() {
    return this.form.controls;
  }

  ngOnInit(): void {
    forkJoin({
      projects: this._projectsService.apiProjectsMyprojectsGet(),
      currencies: this._currenciesService.apiCurrenciesNamesGet(),
      projectTypes: this._projectTypesService.apiProjectTypesGet(),
      euTypes: this._euTypesService.apiEUTypesGet(),
      divisions: this._divisionsService.apiDivisionsGet(),
      legalEntities: this._legalEntitiesService.apiLegalEntitiesGet(),
      productGroups: this._productGroupsService.apiProductGroupsGet(),
    })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(({ projects, currencies, projectTypes, euTypes, divisions, legalEntities, productGroups }) => {
        this.projects = (projects ?? []) as ProjectDTO[];
        this.currencies = currencies ?? [];
        this.projectTypes = (projectTypes ?? []) as LookupDTO[];
        this.euTypes = (euTypes ?? []) as LookupDTO[];
        this.divisions = divisions ?? [];
        this.legalEntities = legalEntities ?? [];
        this.productGroups = productGroups ?? [];
      });

    this.f.legalEntityId.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.onLegalEntityChange());

    this.f.divisionId.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => this.onDivisionChange());
  }

  onLegalEntityChange(): void {
    this.f.divisionId.setValue(null, { emitEvent: false });
    this.f.productGroupId.setValue(null, { emitEvent: false });
    this.f.productGroupId.disable({ emitEvent: false });

    const selectedLegalEntityId = this.f.legalEntityId.value;

    if (!selectedLegalEntityId) {
      this.filteredDivisions = [];
      this.filteredProductGroups = [];
      this.f.divisionId.disable({ emitEvent: false });
      return;
    }

    this.filteredDivisions = this.divisions.filter((d) =>
      this.productGroups.some((pg) => pg.divisionId === d.id && pg.legalEntityId === selectedLegalEntityId)
    );

    this.f.divisionId.enable({ emitEvent: false });
  }

  onDivisionChange(): void {
    this.f.productGroupId.setValue(null, { emitEvent: false });

    const selectedDivisionId = this.f.divisionId.value;
    const selectedLegalEntityId = this.f.legalEntityId.value;

    if (!selectedDivisionId || !selectedLegalEntityId) {
      this.filteredProductGroups = [];
      this.f.productGroupId.disable({ emitEvent: false });
      return;
    }

    this.filteredProductGroups = this.productGroups.filter(
      (pg) => pg.divisionId === selectedDivisionId && pg.legalEntityId === selectedLegalEntityId
    );

    this.f.productGroupId.enable({ emitEvent: false });
  }

  onCancel(): void {
    this._dialogRef.close(null);
  }

  onFinish(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this._dialogRef.close({ ...this.form.getRawValue(), importFile: this.selectedFile });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileDropped(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this._setSelectedFile(file);
    }
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this._setSelectedFile(file);
    }
  }

  clearSelectedFile(event: Event): void {
    event.stopPropagation();
    this.selectedFile = null;
    this.fileError = null;
  }

  private _setSelectedFile(file: File): void {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.fileError = 'File exceeds the maximum size of 2MB';
      this.selectedFile = null;
      return;
    }
    this.fileError = null;
    this.selectedFile = file;
  }
}

